import { LogContext } from './log-error.interface';
import { BreadcrumbService } from '../breadcrumb/breadcrumb.service';
import { stripUrlQuery } from '../breadcrumb/breadcrumb-sanitizer.util';

// Simple in-memory cache for deduplication to prevent flooding
const errorCache = new Set<string>();
const DEDUPE_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Generates a minimal, safe hash for deduplication based on error message and context.
 */
function getErrorHash(error: unknown, context: LogContext): string {
  const errMsg = error instanceof Error ? error.message : String(error);
  const contextStr = context.context ?? '';
  return `${errMsg}:${contextStr}:${context.category}`;
}



/**
 * Centralized utility to log errors gracefully and securely.
 * Designed as a thin wrapper around a third-party telemetry service (e.g., Sentry).
 * 
 * Breadcrumb Integration:
 * The moment an error is caught here, we query BreadcrumbService.getSnapshot(). 
 * This attaches the 20 most recent user actions (navigation, auth, business events) 
 * directly to the error payload to provide critical debugging context.
 * 
 * @param error The Error object or string caught in the catch block.
 * @param context Required typed metadata (LogContext) strictly defining the error taxonomy.
 */
export function logError(error: unknown, context: LogContext): void {
  try {
    // 1. Deduplication (Throttle)
    const hash = getErrorHash(error, context);
    if (errorCache.has(hash)) {
      return; // Skip logging if we've seen this exact error recently
    }
    
    // Add to cache and set timeout to clear it
    errorCache.add(hash);
    setTimeout(() => {
      errorCache.delete(hash);
    }, DEDUPE_TIMEOUT_MS);

    // 2. Build the Allowlisted Payload (Minimal, typed collection)
    // We strictly only collect these fields. We never blindly attach full request/form bodies.
    const safePayload = {
      message: error instanceof Error ? error.message : String(error),
      name: error instanceof Error ? error.name : 'UnknownError',
      stackTrace: error instanceof Error ? error.stack : undefined,
      category: context.category,
      correlationId: context.correlationId,
      componentStack: context.componentStack,
      status: context.status,
      url: stripUrlQuery(context.url ?? (typeof window !== 'undefined' ? window.location.href : undefined)),
      contextString: context.context,
      timestamp: new Date().toISOString(),
      breadcrumbs: BreadcrumbService.getSnapshot()
      // Note: app version can be injected here automatically from env vars
    };

    const isLocalServer = process.env.NODE_ENV === 'development';
    const isTelemetryDisabled = process.env.REACT_APP_DISABLE_TELEMETRY === 'true';

    if (isLocalServer || isTelemetryDisabled) {
      // 3. Development/Stage Behavior: Direct console output
      console.group('%c 🛑 Application Error', 'color: white; background: red; padding: 2px 4px; border-radius: 4px;');
      console.error(error);
      console.log('Safe Payload:', safePayload);
      console.groupEnd();
    } else {
      // 4. Production Behavior: Telemetry logging
      // TODO: Actual Sentry / Telemetry initialization (See SIR-0012 for integration plan)
      
      // Temporarily outputting to console in production to prevent swallowing 
      // until the Sentry SDK is fully integrated and tested.
      console.error('Production Error Intercepted (Telemetry Pending):', error, safePayload);
    }
  } catch (loggingError) {
    // 5. Fail-Safe: If the logger itself fails, it MUST NOT throw and crash the application.
    // We log it even in production so failures aren't swallowed invisibly (ERR-003).
    console.error('Logger Utility Failed:', loggingError);
  }
}
